import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Toaster } from "react-hot-toast";
import { Suspense, lazy } from "react";
import ProtectedRoute from "./routes/ProtectedRoute";
import { ThemeRouteSync } from "./context/ThemeContext";
import { CookieConsentProvider, useCookieConsent } from "./context/CookieConsentContext";
import CookieBanner from "./components/common/CookieBanner";
// Châssis du cockpit directeur (refonte Claude Design) — non lazy : il porte la
// navigation et doit rester monté d'une page directeur à l'autre.
import DirecteurShell from "./components/directeur/DirecteurShell";
import ErrorBoundary from "./components/common/ErrorBoundary";

// Helper lazy loader (préfixe clair pour debugging réseau)
const lazyPage = (loader) => lazy(loader);

// Pages génériques
const Login = lazyPage(() => import("./pages/auth/Login"));
// Marketing
const Landing = lazyPage(() => import("./pages/marketing/Landing"));
const ForgotPassword = lazyPage(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazyPage(() => import("./pages/auth/ResetPassword"));
const Page403 = lazyPage(() => import("./pages/auth/Page403"));
const TimetableAI = lazyPage(() => import("./pages/marketing/TimetableAI"));
const AbsencesRetardsDemo = lazyPage(() => import("./pages/marketing/AbsencesRetardsDemo"));
const NsQuestionnaire = lazyPage(() => import("./pages/marketing/ns_Questionnaire"));

// Élève
const DashboardEleve = lazyPage(() => import("./pages/eleve/DashboardEleve"));
const NotesEleve = lazyPage(() => import("./components/notes/NotesEleve"));
const DevoirsEleve = lazyPage(() => import("./components/devoirs/DevoirsEleve"));
const EmploiDuTempsEleve = lazyPage(() => import("./components/commun/emploiDuTemps/EmploiDuTempsEleve"));
const MessagerieEleve = lazyPage(() => import("./components/commun/messagerie/MessagerieEleve"));
const ProfilEleve = lazyPage(() => import("./pages/eleve/ProfilEleve"));
const QCMPlay = lazyPage(() => import("./components/eleve/QCMPlay"));
const AbsencesEleve = lazyPage(() => import("./pages/eleve/AbsencesEleve"));
const CoursEleve = lazyPage(() => import("./pages/eleve/CoursEleve"));
const CantineEleve = lazyPage(() => import("./pages/eleve/CantineEleve"));
const SortiesEleve = lazyPage(() => import("./pages/eleve/SortiesEleve"));
const TransportEleve = lazyPage(() => import("./pages/eleve/TransportEleve"));
const ConvocationsEleve = lazyPage(() => import("./pages/eleve/ConvocationsEleve"));
const ConventionsEleve = lazyPage(() => import("./pages/eleve/ConventionsEleve"));
const PeriscolaireEleve = lazyPage(() => import("./pages/eleve/PeriscolaireEleve"));
const MaClasseEleve = lazyPage(() => import("./pages/eleve/MaClasseEleve"));
const LivretsEleve = lazyPage(() => import("./pages/eleve/LivretsEleve"));
const SignaturesEleve = lazyPage(() => import("./pages/eleve/SignaturesEleve"));
const InfirmerieEleve = lazyPage(() => import("./pages/eleve/InfirmerieEleve"));
const OrientationEleve = lazyPage(() => import("./pages/eleve/OrientationEleve"));
const StagesEleve = lazyPage(() => import("./pages/eleve/StagesEleve"));
const ExamensBlancsEleve = lazyPage(() => import("./pages/eleve/ExamensBlancsEleve"));
const ClasseVirtuelleEleve = lazyPage(() => import("./pages/eleve/ClasseVirtuelleEleve"));

// Prof
const DashboardProf = lazyPage(() => import("./pages/prof/DashboardProf"));
const DetailClasse = lazyPage(() => import("./components/prof/DetailClasse"));
const EmploiDuTempsProf = lazyPage(() => import("./components/commun/emploiDuTemps/EmploiDuTempsProf"));
const FeuilleAppelProf = lazyPage(() => import("./components/prof/FeuilleAppelProf"));
const ListeClasses = lazyPage(() => import("./components/prof/ListeClasses"));
const SynthesesClasses = lazyPage(() => import("./pages/prof/SynthesesClasses"));
const MessagerieProf = lazyPage(() => import("./components/prof/MessagerieProf"));
const SaisieNotesProf = lazyPage(() => import("./components/prof/SaisieNotesProf"));
const CreerNote = lazyPage(() => import("./pages/prof/CreerNote"));
const DevoirsProf = lazyPage(() => import("./components/prof/DevoirsProf"));
const DevoirsProfMinimal = lazyPage(() => import("./components/prof/DevoirsProfMinimal"));
const QCMBuilder = lazyPage(() => import("./components/prof/QCMBuilder"));
const ProfilProf = lazyPage(() => import("./pages/prof/ProfilProf"));
const CoursContenuProf = lazyPage(() => import("./pages/prof/CoursContenuProf"));
const SanctionsProf = lazyPage(() => import("./pages/prof/SanctionsProf"));
const ConseilsClasseProf = lazyPage(() => import("./pages/prof/ConseilsClasseProf"));
const DisponibilitesProf = lazyPage(() => import("./pages/prof/DisponibilitesProf"));
const MesAbsencesProf = lazyPage(() => import("./pages/prof/MesAbsencesProf"));
const LivretsProf = lazyPage(() => import("./pages/prof/LivretsProf"));
const SignaturesProf = lazyPage(() => import("./pages/prof/SignaturesProf"));
const InfirmerieProf = lazyPage(() => import("./pages/prof/InfirmerieProf"));
const OrientationProf = lazyPage(() => import("./pages/prof/OrientationProf"));
const StagesProf = lazyPage(() => import("./pages/prof/StagesProf"));
const ExamensBlancsProf = lazyPage(() => import("./pages/prof/ExamensBlancsProf"));
const ClasseVirtuelleProf = lazyPage(() => import("./pages/prof/ClasseVirtuelleProf"));

// Parent
const DashboardParent = lazyPage(() => import("./pages/parent/DashboardParent"));
const ActivationParent = lazyPage(() => import("./pages/parent/ActivationParent"));
const ResultatsParent = lazyPage(() => import("./pages/parent/ResultatsParent"));
const AbsencesParent = lazyPage(() => import("./pages/parent/AbsencesParent"));

// School Module
const BuildingsRooms = lazyPage(() => import("./pages/school/BuildingsRooms"));
const Teachers = lazyPage(() => import("./pages/school/Teachers"));
const TeacherDetail = lazyPage(() => import("./pages/school/TeacherDetail"));
const Subjects = lazyPage(() => import("./pages/school/Subjects"));
const Classes = lazyPage(() => import("./pages/school/Classes"));
const Students = lazyPage(() => import("./pages/school/Students"));
const CalendrierScolaire = lazyPage(() => import("./pages/school/CalendrierScolaire"));
const MessagerieParent = lazyPage(() => import("./pages/parent/MessagerieParent"));
const NotesParent = lazyPage(() => import("./pages/parent/NotesParent"));
const ConseilsParent = lazyPage(() => import("./pages/parent/ConseilsParent"));
const ComportementParent = lazyPage(() => import("./pages/parent/ComportementParent"));
const PaiementsParent = lazyPage(() => import("./pages/parent/PaiementsParent"));
const DevoirsParent = lazyPage(() => import("./pages/parent/DevoirsParent"));
const EmploiDuTempsParent = lazyPage(() => import("./pages/parent/EmploiDuTempsParent"));
const CoursParent = lazyPage(() => import("./pages/parent/CoursParent"));
const CantineParent = lazyPage(() => import("./pages/parent/CantineParent"));
const SortiesParent = lazyPage(() => import("./pages/parent/SortiesParent"));
const TransportParent = lazyPage(() => import("./pages/parent/TransportParent"));
const ConvocationsParent = lazyPage(() => import("./pages/parent/ConvocationsParent"));
const ConventionsParent = lazyPage(() => import("./pages/parent/ConventionsParent"));
const PeriscolaireParent = lazyPage(() => import("./pages/parent/PeriscolaireParent"));
const ComparaisonFratrie = lazyPage(() => import("./pages/parent/ComparaisonFratrie"));
const JustificatifsFiscaux = lazyPage(() => import("./pages/parent/JustificatifsFiscaux"));
const SignaturesParent = lazyPage(() => import("./pages/parent/SignaturesParent"));
const InfirmerieParent = lazyPage(() => import("./pages/parent/InfirmerieParent"));
const ProfilParent = lazyPage(() => import("./pages/parent/ProfilParent"));
const LivretsParent = lazyPage(() => import("./pages/parent/LivretsParent"));
const OrientationParent = lazyPage(() => import("./pages/parent/OrientationParent"));
const StagesParent = lazyPage(() => import("./pages/parent/StagesParent"));
const ExamensBlancsParent = lazyPage(() => import("./pages/parent/ExamensBlancsParent"));

// Administratif
const DashboardAdministratif = lazyPage(() => import("./pages/administratif/DashboardAdministratif"));
const DocumentsAdministratif = lazyPage(() => import("./pages/administratif/DocumentsAdministratif"));
const PlanningAdministratif = lazyPage(() => import("./pages/administratif/PlanningAdministratif"));
const SuiviElevesAdministratif = lazyPage(() => import("./pages/administratif/SuiviElevesAdministratif"));
const EmploisDuTempsAdmin = lazyPage(() => import("./pages/administratif/EmploisDuTempsAdmin"));
const ConfigSalles = lazyPage(() => import("./pages/administratif/ConfigSalles"));
const ConfigMatieresClasse = lazyPage(() => import("./pages/administratif/ConfigMatieresClasse"));
const ConfigIndisposProfs = lazyPage(() => import("./pages/administratif/ConfigIndisposProfs"));
const ConfigContraintesEDT = lazyPage(() => import("./pages/administratif/ConfigContraintesEDT"));
const AbsencesElevesAdministratif = lazyPage(() => import("./pages/administratif/AbsencesElevesAdministratif"));
const UtilisateursAdministratif = lazyPage(() => import("./pages/administratif/UtilisateursAdministratif"));
const GestionPaiements = lazyPage(() => import("./pages/administratif/GestionPaiements"));
const GestionCantine = lazyPage(() => import("./pages/administratif/GestionCantine"));
const GestionSorties = lazyPage(() => import("./pages/administratif/GestionSorties"));
const GestionTransport = lazyPage(() => import("./pages/administratif/GestionTransport"));
const GestionConvocations = lazyPage(() => import("./pages/administratif/GestionConvocations"));
const GestionConventions = lazyPage(() => import("./pages/administratif/GestionConventions"));
const GestionPeriscolaire = lazyPage(() => import("./pages/administratif/GestionPeriscolaire"));
const GestionInscriptions = lazyPage(() => import("./pages/administratif/GestionInscriptions"));
const GestionInfirmerie = lazyPage(() => import("./pages/administratif/GestionInfirmerie"));
const SignaturesAdministratif = lazyPage(() => import("./pages/administratif/SignaturesAdministratif"));
const GEDAdministratif = lazyPage(() => import("./pages/administratif/GEDAdministratif"));
const OrientationAdministratif = lazyPage(() => import("./pages/administratif/OrientationAdministratif"));
const StagesAdministratif = lazyPage(() => import("./pages/administratif/StagesAdministratif"));
const ExamensBlancsAdministratif = lazyPage(() => import("./pages/administratif/ExamensBlancsAdministratif"));
const FinancesAvancees = lazyPage(() => import("./pages/administratif/FinancesAvancees"));
const AnnoncesAdministratif = lazyPage(() => import("./pages/administratif/AnnoncesAdministratif"));

// GED par rôle
const GEDProf = lazyPage(() => import("./pages/prof/GEDProf"));
const GEDParent = lazyPage(() => import("./pages/parent/GEDParent"));
const GEDEleve = lazyPage(() => import("./pages/eleve/GEDEleve"));
const GEDDirecteur = lazyPage(() => import("./pages/directeur/GEDDirecteur"));

// Directeur — cockpit refonte Claude Design : les 15 pages sont portées.
const DashboardDirecteur = lazyPage(() => import("./pages/directeur/DashboardDirecteur.jsx"));
const OverviewDirecteur = lazyPage(() => import("./pages/directeur/OverviewDirecteur.jsx"));
const StudentsDirecteur = lazyPage(() => import("./pages/directeur/StudentsDirecteur.jsx"));
const StaffDirecteur = lazyPage(() => import("./pages/directeur/StaffDirecteur.jsx"));
const AttendanceDirecteur = lazyPage(() => import("./pages/directeur/AttendanceDirecteur.jsx"));
const PedagogyDirecteur = lazyPage(() => import("./pages/directeur/PedagogyDirecteur.jsx"));
const BulletinsDirecteur = lazyPage(() => import("./pages/directeur/BulletinsDirecteur.jsx"));
const GenerateurEdtDirecteur = lazyPage(() => import("./pages/directeur/GenerateurEdtDirecteur.jsx"));
const EventsDirecteur = lazyPage(() => import("./pages/directeur/EventsDirecteur.jsx"));
const HrDirecteur = lazyPage(() => import("./pages/directeur/HrDirecteur.jsx"));
const DocumentsDirecteur = lazyPage(() => import("./pages/directeur/DocumentsDirecteur.jsx"));
const ComplianceDirecteur = lazyPage(() => import("./pages/directeur/ComplianceDirecteur.jsx"));
const SettingsDirecteur = lazyPage(() => import("./pages/directeur/SettingsDirecteur.jsx"));
const CommunicationDirecteur = lazyPage(() => import("./pages/directeur/CommunicationDirecteur.jsx"));
const BudgetDirecteur = lazyPage(() => import("./pages/directeur/BudgetDirecteur.jsx"));
const RoomsDirecteur = lazyPage(() => import("./pages/directeur/RoomsDirecteur.jsx"));
const ScheduleDirecteur = lazyPage(() => import("./pages/directeur/ScheduleDirecteur.jsx"));
const StatistiquesDirecteur = lazyPage(() => import("./pages/directeur/StatistiquesDirecteur"));
const SuiviDirecteur = lazyPage(() => import("./pages/directeur/SuiviDirecteur"));
const ConfigurationDirecteur = lazyPage(() => import("./pages/directeur/ConfigurationDirecteur.jsx"));
// Nouveau modules directeur (MVP)
const StructurePedagogique = lazyPage(() => import("./pages/directeur/StructurePedagogique"));
const GestionRH = lazyPage(() => import("./pages/directeur/GestionRH"));
const ReglesPedagogiques = lazyPage(() => import("./pages/directeur/ReglesPedagogiques"));
const CompositionClasses = lazyPage(() => import("./pages/directeur/CompositionClasses"));
const ComptesRendusConseil = lazyPage(() => import("./pages/directeur/ComptesRendusConseil"));
const SynthesesProfils = lazyPage(() => import("./pages/directeur/SynthesesProfils"));
const ImportWizard = lazyPage(() => import("./pages/directeur/ImportWizard"));
const SignaturesDirecteur = lazyPage(() => import("./pages/directeur/SignaturesDirecteur"));
const SportsInfrastructures = lazyPage(() => import("./pages/directeur/SportsInfrastructures"));
const ObjectifsPriorites = lazyPage(() => import("./pages/directeur/ObjectifsPriorites"));
const PreAffectationsVerrous = lazyPage(() => import("./pages/directeur/PreAffectationsVerrous"));
const GouvernanceValidation = lazyPage(() => import("./pages/directeur/GouvernanceValidation"));
const DonneesHistoriques = lazyPage(() => import("./pages/directeur/DonneesHistoriques"));
const LivretsDirecteur = lazyPage(() => import("./pages/directeur/LivretsDirecteur"));
const OrientationDirecteur = lazyPage(() => import("./pages/directeur/OrientationDirecteur"));
const StagesDirecteur = lazyPage(() => import("./pages/directeur/StagesDirecteur"));
const ExamensBlancsDirecteur = lazyPage(() => import("./pages/directeur/ExamensBlancsDirecteur"));
const PersonnalisationTheme = lazyPage(() => import("./pages/directeur/PersonnalisationTheme"));

// Comptabilité établissement
const ComptaDashboard = lazyPage(() => import("./pages/directeur/ComptaDashboard"));
const ComptaBudget = lazyPage(() => import("./pages/directeur/ComptaBudget"));
const ComptaTransactions = lazyPage(() => import("./pages/directeur/ComptaTransactions"));
const ComptaRapprochement = lazyPage(() => import("./pages/directeur/ComptaRapprochement"));
const ComptaExport = lazyPage(() => import("./pages/directeur/ComptaExport"));

// Admin départemental
const DashboardAdmin = lazyPage(() => import("./pages/admin/DashboardAdmin"));
const GestionClasses = lazyPage(() => import("./pages/admin/GestionClasses"));
const GestionClassesAPI = lazyPage(() => import("./pages/admin/GestionClassesAPI"));
const GestionUtilisateurs = lazyPage(() => import("./pages/admin/GestionUtilisateurs"));
const Etablissement = lazyPage(() => import("./pages/admin/Etablissement"));
const Logs = lazyPage(() => import("./pages/admin/Logs"));
const Securite = lazyPage(() => import("./pages/admin/Securite"));
const GestionPasskeys = lazyPage(() => import("./pages/admin/GestionPasskeys"));
const DemoAdmin = lazyPage(() => import("./pages/admin/DemoAdmin"));
const ParentsAdmin = lazyPage(() => import("./pages/admin/ParentsAdmin"));
const BaseEtablissements = lazyPage(() => import("./pages/admin/BaseEtablissements"));
const EtablissementDetail = lazyPage(() => import("./pages/admin/EtablissementDetail"));
const AccountSecurity = lazyPage(() => import("./pages/account/Security"));
const PreferencesPage = lazyPage(() => import("./pages/account/Preferences"));
const ChangePassword = lazyPage(() => import("./pages/account/ChangePassword"));
const VerifyEmail = lazyPage(() => import("./pages/account/VerifyEmail"));

// Super Admin
const SuperAdminDashboard = lazyPage(() => import("./pages/superadmin/DashboardSuperAdmin"));
const SuperAdminHQ = lazyPage(() => import("./pages/superadmin/hq/HQApp"));
const ProfilSuperAdmin = lazyPage(() => import("./pages/superadmin/ProfilSuperAdmin"));
const DemoOfflineEntry = lazyPage(() => import("./pages/superadmin/DemoOfflineEntry"));
// Légal
const Privacy = lazyPage(() => import("./pages/legal/Privacy"));
const Terms = lazyPage(() => import("./pages/legal/Terms"));
const Mentions = lazyPage(() => import("./pages/legal/Mentions"));
const PaiementsSuperAdmin = lazyPage(() => import("./pages/superadmin/PaiementsSuperAdmin"));
const ComptesInactifsSuperAdmin = lazyPage(() => import("./pages/superadmin/ComptesInactifsSuperAdmin"));
const CycleVieEtablissements = lazyPage(() => import("./pages/superadmin/CycleVieEtablissements"));
const ProspectDetail = lazyPage(() => import("./pages/superadmin/ProspectDetail"));
const ConfigurationEssai = lazyPage(() => import("./pages/superadmin/ConfigurationEssai"));
const GestionEtablissements = lazyPage(() => import("./pages/superadmin/GestionEtablissements"));
const Gouvernance = lazyPage(() => import("./pages/superadmin/Gouvernance"));
const ParametragesGlobaux = lazyPage(() => import("./pages/superadmin/ParametragesGlobaux"));
// SuperAdmin > Facturation (Bloc C)
const FacturesEtabliesSuperAdmin = lazyPage(() => import("./pages/superadmin/FacturesEtabliesSuperAdmin"));
const EncaissementsSuperAdmin = lazyPage(() => import("./pages/superadmin/EncaissementsSuperAdmin"));
const RelancesSuperAdmin = lazyPage(() => import("./pages/superadmin/RelancesSuperAdmin"));
const AvoirsSuperAdmin = lazyPage(() => import("./pages/superadmin/AvoirsSuperAdmin"));
// SuperAdmin > Commercial (Bloc C)
const CrmSuperAdmin = lazyPage(() => import("./pages/superadmin/CrmSuperAdmin"));
const DevisCommercialSuperAdmin = lazyPage(() => import("./pages/superadmin/DevisCommercialSuperAdmin"));
const SimulateurTarifaireSuperAdmin = lazyPage(() => import("./pages/superadmin/SimulateurTarifaireSuperAdmin"));
const CatalogueOffresSuperAdmin = lazyPage(() => import("./pages/superadmin/CatalogueOffresSuperAdmin"));
const ContratsCommerciauxSuperAdmin = lazyPage(() => import("./pages/superadmin/ContratsCommerciauxSuperAdmin"));
// SuperAdmin > Plateforme (Bloc C)
const StatutServicesSuperAdmin = lazyPage(() => import("./pages/superadmin/StatutServicesSuperAdmin"));
const PerformanceSuperAdmin = lazyPage(() => import("./pages/superadmin/PerformanceSuperAdmin"));
const LogsGlobauxSuperAdmin = lazyPage(() => import("./pages/superadmin/LogsGlobauxSuperAdmin"));
const SecuriteSuperAdmin = lazyPage(() => import("./pages/superadmin/SecuriteSuperAdmin"));
// SuperAdmin > Clients (Bloc C)
const OnboardingClientsSuperAdmin = lazyPage(() => import("./pages/superadmin/OnboardingClientsSuperAdmin"));
const RenouvellementsSuperAdmin = lazyPage(() => import("./pages/superadmin/RenouvellementsSuperAdmin"));
const ChurnRiskSuperAdmin = lazyPage(() => import("./pages/superadmin/ChurnRiskSuperAdmin"));
// SuperAdmin > Pipeline (Bloc C)
const ProspectsListSuperAdmin = lazyPage(() => import("./pages/superadmin/ProspectsListSuperAdmin"));
const NegociationsSuperAdmin = lazyPage(() => import("./pages/superadmin/NegociationsSuperAdmin"));
const SignaturesSuperAdmin = lazyPage(() => import("./pages/superadmin/SignaturesSuperAdmin"));
// SuperAdmin > Produit (Bloc C)
const RoadmapProduitSuperAdmin = lazyPage(() => import("./pages/superadmin/RoadmapProduitSuperAdmin"));
const ReleasesProduitSuperAdmin = lazyPage(() => import("./pages/superadmin/ReleasesProduitSuperAdmin"));
const DemandesEvolutionSuperAdmin = lazyPage(() => import("./pages/superadmin/DemandesEvolutionSuperAdmin"));
// SuperAdmin > Équipe (Bloc C)
const MembresSchoolsensesSuperAdmin = lazyPage(() => import("./pages/superadmin/MembresSchoolsensesSuperAdmin"));
const ReportingInterneSuperAdmin = lazyPage(() => import("./pages/superadmin/ReportingInterneSuperAdmin"));

// Utilitaire précédemment utilisé (désactivé). Conservé pour référence mais ignoré par ESLint.
// eslint-disable-next-line no-unused-vars
const _secure = (role, Component) => (
  <ProtectedRoute allowedRole={role}>
    <Component />
  </ProtectedRoute>
);

const NotesBulletinsDemo = lazyPage(() => import("./pages/marketing/NotesBulletinsDemo"));
const DevoirsQCMDemo = lazyPage(() => import("./pages/marketing/DevoirsQCMDemo"));
const TimetableAIDemo = lazyPage(() => import("./pages/marketing/TimetableAIDemo"));
const MessagerieDemo = lazyPage(() => import("./pages/marketing/MessagerieDemo"));
const PaymentsInvoicesDemo = lazyPage(() => import("./pages/marketing/PaymentsInvoicesDemo"));
const DemoPage = lazyPage(() => import("./pages/marketing/DemoPage"));
const DevisPage = lazyPage(() => import("./pages/marketing/DevisPage"));
const InviteUsers = lazyPage(() => import("./pages/public/InviteUsers"));
const ListeEtablissements = lazyPage(() => import("./pages/public/ListeEtablissements"));
const VitrineEtablissement = lazyPage(() => import("./pages/public/VitrineEtablissement"));
const FormulaireInscription = lazyPage(() => import("./pages/public/FormulaireInscription"));
const SuiviInscription = lazyPage(() => import("./pages/public/SuiviInscription"));
const InscriptionsParent = lazyPage(() => import("./pages/parent/InscriptionsParent"));
const RendezVousPage = lazyPage(() => import("./pages/rdv/RendezVousPage"));

function ConsentGatedAnalytics() {
  const { consent } = useCookieConsent();
  if (consent !== "accepted") return null;
  return (
    <>
      <Analytics />
      <SpeedInsights />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <CookieConsentProvider>
        <ThemeRouteSync />
        <Toaster position="top-center" toastOptions={{ duration: 2500 }} />
        <ConsentGatedAnalytics />
        <CookieBanner />
      <ErrorBoundary>
      <Suspense fallback={<div style={{padding:'2rem',textAlign:'center'}}>Chargement...</div>}>
      <Routes>
        {/* Public invite route for establishments */}
        <Route path="/invite/:token" element={<InviteUsers />} />
        {/* Marketing */}
        <Route path="/" element={<Landing />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/devis" element={<DevisPage />} />
          <Route path="/questionnaire" element={<NsQuestionnaire />} />
  <Route path="/ia/emplois-du-temps" element={<TimetableAI />} />
  <Route path="/demo/emplois-du-temps" element={<TimetableAIDemo />} />
  <Route path="/demo/absences-retards" element={<AbsencesRetardsDemo />} />
  <Route path="/demo/notes-bulletins" element={<NotesBulletinsDemo />} />
  <Route path="/demo/devoirs-qcm" element={<DevoirsQCMDemo />} />
  <Route path="/demo/messagerie" element={<MessagerieDemo />} />
  <Route path="/demo/paiements-factures" element={<PaymentsInvoicesDemo />} />
        {/* Inscriptions publiques */}
        <Route path="/etablissements" element={<ListeEtablissements />} />
        <Route path="/etablissement/:slug" element={<VitrineEtablissement />} />
        <Route path="/inscription/:etablissementId" element={<FormulaireInscription />} />
        <Route path="/inscription/suivi/:token" element={<SuiviInscription />} />

        {/* Connexion */}
        <Route path="/login" element={<Login />} />
  <Route path="/forgot-password" element={<ForgotPassword />} />
  <Route path="/reset-password" element={<ResetPassword />} />
  <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Compte */}
        <Route
          path="/account/security"
          element={
            <ProtectedRoute>
              <AccountSecurity />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/preferences"
          element={
            <ProtectedRoute>
              <PreferencesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          }
        />

        {/* Élève */}
        <Route
          path="/eleve"
          element={
            <ProtectedRoute allowedRole="eleve">
              <DashboardEleve />
            </ProtectedRoute>
          }
        />

        <Route
  path="/eleve/devoirs"
  element={
    <ProtectedRoute allowedRole="eleve">
      <DevoirsEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/notes"
  element={
    <ProtectedRoute allowedRole="eleve">
      <NotesEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/absences"
  element={
    <ProtectedRoute allowedRole="eleve">
      <AbsencesEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/cours"
  element={
    <ProtectedRoute allowedRole="eleve">
      <CoursEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/emploi-du-temps"
  element={
    <ProtectedRoute allowedRole="eleve">
      <EmploiDuTempsEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/messagerie"
  element={
    <ProtectedRoute allowedRole="eleve">
      <MessagerieEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/profil"
  element={
    <ProtectedRoute allowedRole="eleve">
      <ProfilEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/qcm/:id"
  element={
    <ProtectedRoute allowedRole="eleve">
      <QCMPlay />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/cantine"
  element={
    <ProtectedRoute allowedRole="eleve">
      <CantineEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/sorties"
  element={
    <ProtectedRoute allowedRole="eleve">
      <SortiesEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/transport"
  element={
    <ProtectedRoute allowedRole="eleve">
      <TransportEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/convocations"
  element={
    <ProtectedRoute allowedRole="eleve">
      <ConvocationsEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/conventions"
  element={
    <ProtectedRoute allowedRole="eleve">
      <ConventionsEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/periscolaire"
  element={
    <ProtectedRoute allowedRole="eleve">
      <PeriscolaireEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/ma-classe"
  element={
    <ProtectedRoute allowedRole="eleve">
      <MaClasseEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/signatures"
  element={
    <ProtectedRoute allowedRole="eleve">
      <SignaturesEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/ged"
  element={
    <ProtectedRoute allowedRole="eleve">
      <GEDEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/infirmerie"
  element={
    <ProtectedRoute allowedRole="eleve">
      <InfirmerieEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/livrets"
  element={
    <ProtectedRoute allowedRole="eleve">
      <LivretsEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/orientation"
  element={
    <ProtectedRoute allowedRole="eleve">
      <OrientationEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/stages"
  element={
    <ProtectedRoute allowedRole="eleve">
      <StagesEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/examens-blancs"
  element={
    <ProtectedRoute allowedRole="eleve">
      <ExamensBlancsEleve />
    </ProtectedRoute>
  }
/>
<Route
  path="/eleve/classe-virtuelle"
  element={
    <ProtectedRoute allowedRole="eleve">
      <ClasseVirtuelleEleve />
    </ProtectedRoute>
  }
/>

        {/* Prof */}
        <Route
          path="/prof"
          element={
            <ProtectedRoute allowedRole="prof">
              <DashboardProf />
            </ProtectedRoute>
          }
        />
        <Route
          path="/prof/profil"
          element={
            <ProtectedRoute allowedRole="prof">
              <ProfilProf />
            </ProtectedRoute>
          }
        />
        <Route
          path="/prof/classe/:nomClasse"
          element={
            <ProtectedRoute allowedRole="prof">
              <DetailClasse />
            </ProtectedRoute>
          }
        />
        <Route
  path="/prof/emploi-du-temps"
  element={
    <ProtectedRoute allowedRole="prof">
      <EmploiDuTempsProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/absences"
  element={
    <ProtectedRoute allowedRole="prof">
      <MesAbsencesProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/rh"
  element={
    <ProtectedRoute allowedRole="prof">
      <GestionRH />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/appels"
  element={
    <ProtectedRoute allowedRole="prof">
      <FeuilleAppelProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/classes"
  element={
    <ProtectedRoute allowedRole="prof">
      <ListeClasses />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/syntheses"
  element={
    <ProtectedRoute allowedRole="prof">
      <SynthesesClasses />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/messagerie"
  element={
    <ProtectedRoute allowedRole="prof">
      <MessagerieProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/devoirs"
  element={
    <ProtectedRoute allowedRole="prof">
      <DevoirsProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/cours-contenu"
  element={
    <ProtectedRoute allowedRole="prof">
      <CoursContenuProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/sanctions"
  element={
    <ProtectedRoute allowedRole="prof">
      <SanctionsProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/conseils"
  element={
    <ProtectedRoute allowedRole="prof">
      <ConseilsClasseProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/disponibilites"
  element={
    <ProtectedRoute allowedRole="prof">
      <DisponibilitesProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/devoirs/minimal"
  element={
    <ProtectedRoute allowedRole="prof">
      <DevoirsProfMinimal />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/qcm/:id"
  element={
    <ProtectedRoute allowedRole="prof">
      <QCMBuilder />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/notes"
  element={
    <ProtectedRoute allowedRole="prof">
      <SaisieNotesProf />
    </ProtectedRoute>
  }
  />
<Route
  path="/prof/livrets"
  element={
    <ProtectedRoute allowedRole="prof">
      <LivretsProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/note/nouvelle"
  element={
    <ProtectedRoute allowedRole="prof">
      <CreerNote />
    </ProtectedRoute>
  }
  />
<Route
  path="/prof/signatures"
  element={
    <ProtectedRoute allowedRole="prof">
      <SignaturesProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/ged"
  element={
    <ProtectedRoute allowedRole="prof">
      <GEDProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/infirmerie"
  element={
    <ProtectedRoute allowedRole="prof">
      <InfirmerieProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/orientation"
  element={
    <ProtectedRoute allowedRole="prof">
      <OrientationProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/stages"
  element={
    <ProtectedRoute allowedRole="prof">
      <StagesProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/examens-blancs"
  element={
    <ProtectedRoute allowedRole="prof">
      <ExamensBlancsProf />
    </ProtectedRoute>
  }
/>
<Route
  path="/prof/classe-virtuelle"
  element={
    <ProtectedRoute allowedRole="prof">
      <ClasseVirtuelleProf />
    </ProtectedRoute>
  }
/>

        {/* Parent */}
        <Route
          path="/parent"
          element={
            <ProtectedRoute allowedRole="parent">
              <DashboardParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/resultats"
          element={
            <ProtectedRoute allowedRole="parent">
              <ResultatsParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/absences"
          element={
            <ProtectedRoute allowedRole="parent">
              <AbsencesParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/emploi-du-temps"
          element={
            <ProtectedRoute allowedRole="parent">
              <EmploiDuTempsParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/messagerie"
          element={
            <ProtectedRoute allowedRole="parent">
              <MessagerieParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/notes"
          element={
            <ProtectedRoute allowedRole="parent">
              <NotesParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/devoirs"
          element={
            <ProtectedRoute allowedRole="parent">
              <DevoirsParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/cours"
          element={
            <ProtectedRoute allowedRole="parent">
              <CoursParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/conseils"
          element={
            <ProtectedRoute allowedRole="parent">
              <ConseilsParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/comportement"
          element={
            <ProtectedRoute allowedRole="parent">
              <ComportementParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/paiements"
          element={
            <ProtectedRoute allowedRole="parent">
              <PaiementsParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/profil"
          element={
            <ProtectedRoute allowedRole="parent">
              <ProfilParent />
            </ProtectedRoute>
          }
        />
<Route
          path="/parent/cantine"
          element={
            <ProtectedRoute allowedRole="parent">
              <CantineParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/sorties"
          element={
            <ProtectedRoute allowedRole="parent">
              <SortiesParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/transport"
          element={
            <ProtectedRoute allowedRole="parent">
              <TransportParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/convocations"
          element={
            <ProtectedRoute allowedRole="parent">
              <ConvocationsParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/conventions"
          element={
            <ProtectedRoute allowedRole="parent">
              <ConventionsParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/periscolaire"
          element={
            <ProtectedRoute allowedRole="parent">
              <PeriscolaireParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/comparaison-fratrie"
          element={
            <ProtectedRoute allowedRole="parent">
              <ComparaisonFratrie />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/justificatifs-fiscaux"
          element={
            <ProtectedRoute allowedRole="parent">
              <JustificatifsFiscaux />
            </ProtectedRoute>
          }
        />
  <Route path="/parent/activation" element={<ActivationParent />} />
        <Route
          path="/parent/inscriptions"
          element={
            <ProtectedRoute allowedRole="parent">
              <InscriptionsParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/livrets"
          element={
            <ProtectedRoute allowedRole="parent">
              <LivretsParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/signatures"
          element={
            <ProtectedRoute allowedRole="parent">
              <SignaturesParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/ged"
          element={
            <ProtectedRoute allowedRole="parent">
              <GEDParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/infirmerie"
          element={
            <ProtectedRoute allowedRole="parent">
              <InfirmerieParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/orientation"
          element={
            <ProtectedRoute allowedRole="parent">
              <OrientationParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/stages"
          element={
            <ProtectedRoute allowedRole="parent">
              <StagesParent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/examens-blancs"
          element={
            <ProtectedRoute allowedRole="parent">
              <ExamensBlancsParent />
            </ProtectedRoute>
          }
        />

        {/* Administratif */}
        <Route
          path="/administratif"
          element={
            <ProtectedRoute allowedRole="administratif">
              <DashboardAdministratif />
            </ProtectedRoute>
          }
        />
        <Route
  path="/administratif/absences"
  element={
    <ProtectedRoute allowedRole="administratif">
      <AbsencesElevesAdministratif />
    </ProtectedRoute>
  }
/>
        <Route
  path="/administratif/documents"
  element={
    <ProtectedRoute allowedRole="administratif">
      <DocumentsAdministratif />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/signatures"
  element={
    <ProtectedRoute allowedRole="administratif">
      <SignaturesAdministratif />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/planning"
  element={
    <ProtectedRoute allowedRole="administratif">
      <PlanningAdministratif />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/suivi"
  element={
    <ProtectedRoute allowedRole="administratif">
      <SuiviElevesAdministratif />
    </ProtectedRoute>
  }
/>
        <Route
          path="/admin/parents"
          element={
            <ProtectedRoute allowedRole="admin">
              <ParentsAdmin />
            </ProtectedRoute>
          }
        />
<Route
  path="/administratif/emplois"
  element={
    <ProtectedRoute allowedRole="administratif">
      <EmploisDuTempsAdmin />
    </ProtectedRoute>
  }
  />
<Route
  path="/administratif/utilisateurs"
  element={
    <ProtectedRoute allowedRole="administratif">
      <UtilisateursAdministratif />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/config-salles"
  element={
    <ProtectedRoute allowedRole="administratif">
      <ConfigSalles />
    </ProtectedRoute>
  }
/> 
<Route
  path="/administratif/config-matieres"
  element={
    <ProtectedRoute allowedRole="administratif">
      <ConfigMatieresClasse />
    </ProtectedRoute>
  }
/> 
<Route
  path="/administratif/config-indispos"
  element={
    <ProtectedRoute allowedRole="administratif">
      <ConfigIndisposProfs />
    </ProtectedRoute>
  }
/> 
<Route
  path="/administratif/config-contraintes"
  element={
    <ProtectedRoute allowedRole="administratif">
      <ConfigContraintesEDT />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/paiements"
  element={
    <ProtectedRoute allowedRole="administratif">
      <GestionPaiements />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/cantine"
  element={
    <ProtectedRoute allowedRole="administratif">
      <GestionCantine />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/sorties"
  element={
    <ProtectedRoute allowedRole="administratif">
      <GestionSorties />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/transport"
  element={
    <ProtectedRoute allowedRole="administratif">
      <GestionTransport />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/convocations"
  element={
    <ProtectedRoute allowedRole="administratif">
      <GestionConvocations />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/conventions"
  element={
    <ProtectedRoute allowedRole="administratif">
      <GestionConventions />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/periscolaire"
  element={
    <ProtectedRoute allowedRole="administratif">
      <GestionPeriscolaire />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/inscriptions"
  element={
    <ProtectedRoute allowedRole="administratif">
      <GestionInscriptions />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/ged"
  element={
    <ProtectedRoute allowedRole="administratif">
      <GEDAdministratif />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/infirmerie"
  element={
    <ProtectedRoute allowedRole="administratif">
      <GestionInfirmerie />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/rh"
  element={
    <ProtectedRoute allowedRole="administratif">
      <GestionRH />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/orientation"
  element={
    <ProtectedRoute allowedRole="administratif">
      <OrientationAdministratif />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/stages"
  element={
    <ProtectedRoute allowedRole="administratif">
      <StagesAdministratif />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/examens-blancs"
  element={
    <ProtectedRoute allowedRole="administratif">
      <ExamensBlancsAdministratif />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/finances-avancees"
  element={
    <ProtectedRoute allowedRole="administratif">
      <FinancesAvancees />
    </ProtectedRoute>
  }
/>
<Route
  path="/administratif/annonces"
  element={
    <ProtectedRoute allowedRole="administratif">
      <AnnoncesAdministratif />
    </ProtectedRoute>
  }
/>

        {/* Directeur — cockpit refonte Claude Design.
            Le shell (sidebar + header du design) est monté par la route parente
            et RESTE monté d'une page à l'autre : seul <Outlet/> change. */}
        <Route
          path="/directeur"
          element={
            <ProtectedRoute allowedRole="directeur">
              <DirecteurShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardDirecteur />} />
          <Route path="overview" element={<OverviewDirecteur />} />
          <Route path="students" element={<StudentsDirecteur />} />
          <Route path="staff" element={<StaffDirecteur />} />
          <Route path="rooms" element={<RoomsDirecteur />} />
          <Route path="schedule" element={<ScheduleDirecteur />} />
          <Route path="pedagogy" element={<PedagogyDirecteur />} />
          {/* Sous-ecran de Pedagogie : le design ne prevoit pas d entree de menu. */}
          <Route path="pedagogy/bulletins" element={<BulletinsDirecteur />} />
          {/* Sous-ecran de l EDT : le module de generation reel (solveur
              OR-Tools), jusqu ici accessible au seul role administratif. */}
          <Route path="schedule/generateur" element={<GenerateurEdtDirecteur />} />
          <Route path="attendance" element={<AttendanceDirecteur />} />
          <Route path="communication" element={<CommunicationDirecteur />} />
          <Route path="events" element={<EventsDirecteur />} />
          <Route path="budget" element={<BudgetDirecteur />} />
          <Route path="hr" element={<HrDirecteur />} />
          <Route path="documents" element={<DocumentsDirecteur />} />
          <Route path="compliance" element={<ComplianceDirecteur />} />
          <Route path="settings" element={<SettingsDirecteur />} />
        </Route>

        {/* Anciennes routes directeur — conservées temporairement, purge en fin de Phase 2. */}
        <Route
          path="/directeur/statistiques"
          element={
            <ProtectedRoute allowedRole="directeur">
              <StatistiquesDirecteur />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/rh"
          element={
            <ProtectedRoute allowedRole="directeur">
              <GestionRH />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/suivi"
          element={
            <ProtectedRoute allowedRole="directeur">
              <SuiviDirecteur />
            </ProtectedRoute>
          }
        />
        <Route
  path="/directeur/configuration"
  element={
    <ProtectedRoute allowedRole="directeur">
      <ConfigurationDirecteur />
    </ProtectedRoute>
  }
/>
        <Route
          path="/directeur/structure"
          element={
            <ProtectedRoute allowedRole="directeur">
              <StructurePedagogique />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/salles"
          element={
            // Ancienne page « Salles & équipements » (thème sombre, suppression sans
            // confirmation) : remplacée par le référentiel du cockpit directeur.
            <Navigate to="/directeur/rooms" replace />
          }
        />
        <Route
          path="/directeur/regles"
          element={
            <ProtectedRoute allowedRole="directeur">
              <ReglesPedagogiques />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/composition-classes"
          element={
            <ProtectedRoute allowedRole="directeur">
              <CompositionClasses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/conseil"
          element={
            <ProtectedRoute allowedRole="directeur">
              <ComptesRendusConseil />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/profils"
          element={
            <ProtectedRoute allowedRole="directeur">
              <SynthesesProfils />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/import"
          element={
            <ProtectedRoute allowedRole="directeur">
              <ImportWizard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/sports"
          element={
            <ProtectedRoute allowedRole="directeur">
              <SportsInfrastructures />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/objectifs"
          element={
            <ProtectedRoute allowedRole="directeur">
              <ObjectifsPriorites />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/pre-affectations"
          element={
            <ProtectedRoute allowedRole="directeur">
              <PreAffectationsVerrous />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/gouvernance"
          element={
            <ProtectedRoute allowedRole="directeur">
              <GouvernanceValidation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/historiques"
          element={
            <ProtectedRoute allowedRole="directeur">
              <DonneesHistoriques />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/signatures"
          element={
            <ProtectedRoute allowedRole="directeur">
              <SignaturesDirecteur />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/ged"
          element={
            <ProtectedRoute allowedRole="directeur">
              <GEDDirecteur />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/livrets"
          element={
            <ProtectedRoute allowedRole="directeur">
              <LivretsDirecteur />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/orientation"
          element={
            <ProtectedRoute allowedRole="directeur">
              <OrientationDirecteur />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/stages"
          element={
            <ProtectedRoute allowedRole="directeur">
              <StagesDirecteur />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/examens-blancs"
          element={
            <ProtectedRoute allowedRole="directeur">
              <ExamensBlancsDirecteur />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directeur/personnalisation"
          element={
            <ProtectedRoute allowedRole="directeur">
              <PersonnalisationTheme />
            </ProtectedRoute>
          }
        />

        {/* Comptabilité — Directeur */}
        <Route path="/directeur/compta" element={<ProtectedRoute allowedRole="directeur"><ComptaDashboard /></ProtectedRoute>} />
        <Route path="/directeur/compta/budget" element={<ProtectedRoute allowedRole="directeur"><ComptaBudget /></ProtectedRoute>} />
        <Route path="/directeur/compta/transactions" element={<ProtectedRoute allowedRole="directeur"><ComptaTransactions /></ProtectedRoute>} />
        <Route path="/directeur/compta/rapprochement" element={<ProtectedRoute allowedRole="directeur"><ComptaRapprochement /></ProtectedRoute>} />
        <Route path="/directeur/compta/export" element={<ProtectedRoute allowedRole="directeur"><ComptaExport /></ProtectedRoute>} />

        {/* Comptabilité — Administratif */}
        <Route path="/administratif/compta" element={<ProtectedRoute allowedRole="administratif"><ComptaDashboard /></ProtectedRoute>} />
        <Route path="/administratif/compta/budget" element={<ProtectedRoute allowedRole="administratif"><ComptaBudget /></ProtectedRoute>} />
        <Route path="/administratif/compta/transactions" element={<ProtectedRoute allowedRole="administratif"><ComptaTransactions /></ProtectedRoute>} />
        <Route path="/administratif/compta/rapprochement" element={<ProtectedRoute allowedRole="administratif"><ComptaRapprochement /></ProtectedRoute>} />
        <Route path="/administratif/compta/export" element={<ProtectedRoute allowedRole="administratif"><ComptaExport /></ProtectedRoute>} />


  {/* Admin départemental */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRole="admin">
              <DashboardAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/base-etablissements"
          element={
            <ProtectedRoute allowedRole="admin">
              <BaseEtablissements />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/etablissements/:id"
          element={
            <ProtectedRoute allowedRole="admin">
              <EtablissementDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/classes"
          element={
            <ProtectedRoute allowedRole="admin">
              <GestionClasses />
                    <Route
                      path="/admin/classes/matieres"
                      element={
                        <ProtectedRoute allowedRole="admin">
                          <GestionClassesAPI />
                        </ProtectedRoute>
                      }
                    />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/utilisateurs"
          element={
            <ProtectedRoute allowedRole="admin">
              <GestionUtilisateurs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/etablissement"
          element={
            <ProtectedRoute allowedRole="admin">
              <Etablissement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/logs"
          element={
            <ProtectedRoute allowedRole="admin">
              <Logs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/securite"
          element={
            <ProtectedRoute allowedRole="admin">
              <Securite />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/passkeys"
          element={
            <ProtectedRoute allowedRole={["admin", "superadmin"]}>
              <GestionPasskeys />
            </ProtectedRoute>
          }
        />

        {/* Admin demo scheduler (temporarily public; secure later) */}
        <Route path="/admin/demos" element={<DemoAdmin />} />

        {/* SuperAdmin */}
        <Route path="/superadmin" element={
          <ProtectedRoute allowedRole="superadmin">
            <Navigate to="/superadmin/dashboard" replace />
          </ProtectedRoute>
        } />

        <Route
          path="/superadmin/profil"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <ProfilSuperAdmin />
            </ProtectedRoute>
          }
        />

        {/* SuperAdmin - Démo: bascule le site complet en mode offline (données en dur) */}
        <Route
          path="/superadmin/demo"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <DemoOfflineEntry />
            </ProtectedRoute>
          }
        />

        <Route
          path="/superadmin/dashboard"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <SuperAdminHQ />
            </ProtectedRoute>
          }
        />
        {/* Ancien dashboard SuperAdmin conservé en accès direct */}
        <Route
          path="/superadmin/dashboard-legacy"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <SuperAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/demos"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <DemoAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/cycle-vie"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <CycleVieEtablissements />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/prospect/:id"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <ProspectDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/configurer-essai/:id"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <ConfigurationEssai />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/etablissements"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <GestionEtablissements />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/gouvernance"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <Gouvernance />
            </ProtectedRoute>
          }
        />
        {/* Ancienne URL conservée en redirection pour compatibilité */}
        <Route
          path="/superadmin/roles-acces"
          element={<Navigate to="/superadmin/gouvernance" replace />}
        />
        <Route
          path="/superadmin/parametres"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <ParametragesGlobaux />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/paiements"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <PaiementsSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/comptes-inactifs"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <ComptesInactifsSuperAdmin />
            </ProtectedRoute>
          }
        />

        {/* SuperAdmin > Facturation (Bloc C) */}
        <Route
          path="/superadmin/facturation/factures"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <FacturesEtabliesSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/facturation/encaissements"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <EncaissementsSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/facturation/relances"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <RelancesSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/facturation/avoirs"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <AvoirsSuperAdmin />
            </ProtectedRoute>
          }
        />

        {/* SuperAdmin > Commercial (Bloc C) */}
        <Route
          path="/superadmin/commercial/crm"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <CrmSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/commercial/devis"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <DevisCommercialSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/commercial/simulateur"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <SimulateurTarifaireSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/commercial/catalogue"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <CatalogueOffresSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/commercial/contrats"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <ContratsCommerciauxSuperAdmin />
            </ProtectedRoute>
          }
        />

        {/* SuperAdmin > Plateforme (Bloc C) */}
        <Route
          path="/superadmin/plateforme/statut"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <StatutServicesSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/plateforme/performance"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <PerformanceSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/plateforme/logs"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <LogsGlobauxSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/plateforme/securite"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <SecuriteSuperAdmin />
            </ProtectedRoute>
          }
        />

        {/* SuperAdmin > Clients (Bloc C) */}
        <Route
          path="/superadmin/clients/onboarding"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <OnboardingClientsSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/clients/renouvellements"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <RenouvellementsSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/clients/churn"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <ChurnRiskSuperAdmin />
            </ProtectedRoute>
          }
        />

        {/* SuperAdmin > Pipeline (Bloc C) */}
        <Route
          path="/superadmin/pipeline/prospects"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <ProspectsListSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/pipeline/negociations"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <NegociationsSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/pipeline/signatures"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <SignaturesSuperAdmin />
            </ProtectedRoute>
          }
        />

        {/* SuperAdmin > Produit (Bloc C) */}
        <Route
          path="/superadmin/produit/roadmap"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <RoadmapProduitSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/produit/releases"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <ReleasesProduitSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/produit/demandes-evolution"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <DemandesEvolutionSuperAdmin />
            </ProtectedRoute>
          }
        />

        {/* SuperAdmin > Équipe (Bloc C) */}
        <Route
          path="/superadmin/equipe/membres"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <MembresSchoolsensesSuperAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin/equipe/reporting"
          element={
            <ProtectedRoute allowedRole="superadmin">
              <ReportingInterneSuperAdmin />
            </ProtectedRoute>
          }
        />

        {/* School Module */}
        <Route
          path="/school/buildings-rooms"
          element={
            <ProtectedRoute allowedRole="administratif">
              <BuildingsRooms />
            </ProtectedRoute>
          }
        />
        <Route
          path="/school/teachers"
          element={
            <ProtectedRoute allowedRole="administratif">
              <Teachers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/school/teachers/:id"
          element={
            <ProtectedRoute allowedRole="administratif">
              <TeacherDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/school/subjects"
          element={
            <ProtectedRoute allowedRole="administratif">
              <Subjects />
            </ProtectedRoute>
          }
        />
        <Route
          path="/school/classes"
          element={
            <ProtectedRoute allowedRole="administratif">
              <Classes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/school/students"
          element={
            <ProtectedRoute allowedRole="administratif">
              <Students />
            </ProtectedRoute>
          }
        />
        <Route
          path="/school/calendrier"
          element={
            <ProtectedRoute allowedRole="administratif">
              <CalendrierScolaire />
            </ProtectedRoute>
          }
        />
        

        {/* Accès refusé */}
        <Route path="/403" element={<Page403 />} />
        {/* Légal */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/mentions" element={<Mentions />} />

        {/* RDV (tous rôles authentifiés) */}
        <Route
          path="/rdv"
          element={
            <ProtectedRoute>
              <RendezVousPage />
            </ProtectedRoute>
          }
        />

        {/* Catch-all : redirige les routes inconnues vers /login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
      </CookieConsentProvider>
    </BrowserRouter>
  );
}

export default App;
